/**
 * Frontier Models — research + news intelligence on the labs pushing the SOTA
 * wavefront. Curated catalogue + per-lab news filter + GitHub deep-link.
 *
 * Data:
 *   /api/frontier/labs   — catalogue
 *   /api/frontier/news   — news filtered to mentions of frontier orgs / models
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Brain, Globe as GlobeIcon, ExternalLink, Github, Newspaper, RefreshCw, Search,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Lab {
  id: string
  name: string
  category: string
  country: string
  city: string
  lat: number; lng: number
  founders: string[]
  founded: number
  models: string[]
  latest: string
  programme: string
  github_org: string
  hf_author?: string
  homepage: string
  tags: string[]
  tier?: 'frontier' | 'specialist-modal' | 'specialist-vert' | 'infrastructure' | 'regulator' | 'alignment' | 'lab'
  // Live enrichment from /api/frontier/labs (HF + GitHub Releases)
  latest_live?: string
  latest_source?: 'huggingface' | 'github-release' | string
  latest_observed?: string
  latest_modified?: string
  latest_live_url?: string
}

interface FrontierArticle {
  id: number
  title: string
  summary: string
  source: string
  url?: string | null
  category: string
  region: string
  labs: string[]
  published_at: string | null
}

const COUNTRY_FLAG: Record<string, string> = {
  US: '🇺🇸', GB: '🇬🇧', FR: '🇫🇷', DE: '🇩🇪', CN: '🇨🇳', JP: '🇯🇵',
  IN: '🇮🇳', CA: '🇨🇦', AU: '🇦🇺', SG: '🇸🇬',
}

const TIER_BADGE: Record<string, { label: string; cls: string; title: string }> = {
  frontier:           { label: 'frontier',     cls: 'bg-purple/10 text-purple-light border-purple/30',
                        title: 'General-purpose foundation model competing on SOTA capability' },
  'specialist-modal': { label: 'modality',     cls: 'bg-blue/10 text-blue-light border-blue/30',
                        title: 'Specialist in a single modality (image / video / audio / voice)' },
  'specialist-vert':  { label: 'vertical',     cls: 'bg-amber/10 text-amber-light border-amber/30',
                        title: 'Domain-specialist (financial / code / agents / enterprise)' },
  infrastructure:     { label: 'infra',        cls: 'bg-cyan/10 text-cyan border-cyan/30',
                        title: 'Compute / inference / model hub' },
  regulator:          { label: 'regulator',    cls: 'bg-emerald/10 text-emerald border-emerald/30',
                        title: 'AI Safety Institute / regulator' },
  alignment:          { label: 'alignment',    cls: 'bg-rose/10 text-rose border-rose/30',
                        title: 'Independent alignment / interpretability research' },
  lab:                { label: 'lab',          cls: 'bg-surface-raised text-text-muted border-border',
                        title: 'AI lab' },
}

export default function FrontierModelsPage() {
  const [labs, setLabs] = useState<Lab[]>([])
  const [news, setNews] = useState<FrontierArticle[]>([])
  const [liveCount, setLiveCount] = useState(0)
  const [liveRefreshedAt, setLiveRefreshedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [refreshingLive, setRefreshingLive] = useState(false)
  const [filter, setFilter] = useState('')
  const [activeLab, setActiveLab] = useState<string | null>(null)
  const [tierFilter, setTierFilter] = useState<string>('')

  const refresh = () => {
    setLoading(true)
    Promise.all([
      api.get('/api/frontier/labs').then(r => {
        setLabs(r.data?.labs || [])
        setLiveCount(r.data?.live_count || 0)
        setLiveRefreshedAt(r.data?.live_refreshed_at || null)
      }).catch(() => {}),
      api.get('/api/frontier/news', { params: { limit: 80 } }).then(r => setNews(r.data?.articles || [])).catch(() => {}),
    ]).finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  const refreshLive = async () => {
    setRefreshingLive(true)
    try {
      await api.post('/api/frontier/labs/refresh-live')
      // Re-pull /labs to merge the freshly-cached live data into the UI
      const r = await api.get('/api/frontier/labs')
      setLabs(r.data?.labs || [])
      setLiveCount(r.data?.live_count || 0)
      setLiveRefreshedAt(r.data?.live_refreshed_at || null)
    } catch { /* surfaced via no-change */ }
    finally { setRefreshingLive(false) }
  }

  const labsById: Record<string, Lab> = useMemo(
    () => Object.fromEntries(labs.map(l => [l.id, l])),
    [labs],
  )

  const filteredLabs = useMemo(() => {
    let out = labs
    if (tierFilter) out = out.filter(l => (l.tier || 'lab') === tierFilter)
    if (filter) {
      const q = filter.toLowerCase()
      out = out.filter(l =>
        l.name.toLowerCase().includes(q) ||
        l.country.toLowerCase() === q ||
        l.tags.some(t => t.includes(q)) ||
        l.models.some(m => m.toLowerCase().includes(q))
      )
    }
    return out
  }, [labs, filter, tierFilter])

  const tierCounts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const l of labs) c[l.tier || 'lab'] = (c[l.tier || 'lab'] || 0) + 1
    return c
  }, [labs])

  const filteredNews = useMemo(() => {
    if (!activeLab) return news
    return news.filter(a => a.labs.includes(activeLab))
  }, [news, activeLab])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Brain size={18} className="text-purple-light" /> FRONTIER MODELS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          labs · models · research · regulatory
        </span>
        <Link to="/app/frontier-globe" className="text-[11px] text-blue-light hover:underline inline-flex items-center gap-1 ml-auto">
          <GlobeIcon size={12} /> Open Frontier Globe
        </Link>
        <button type="button" onClick={refreshLive} disabled={refreshingLive} className="btn-ghost"
          title="Pull latest model versions from HuggingFace + GitHub Releases">
          <RefreshCw size={11} className={refreshingLive ? 'animate-spin' : ''} /> Refresh live versions
        </button>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {liveRefreshedAt && (
        <div className="text-[10px] text-text-muted">
          Live data: {liveCount} of {labs.length} labs · last refresh {liveRefreshedAt.slice(0, 16).replace('T', ' ')} UTC
        </div>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Labs tracked"      value={labs.length} />
        <Tile label="Recent news items" value={news.length} />
        <Tile label="Countries"         value={new Set(labs.map(l => l.country)).size} />
        <Tile label="Open-weight labs"  value={labs.filter(l => l.tags.includes('open-weights')).length} tone="emerald" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <input className="input pl-7 w-[260px]"
            placeholder="Filter labs by name / country / model"
            value={filter} onChange={e => setFilter(e.target.value)}
            aria-label="Filter frontier labs" />
        </div>
        <select className="input" value={tierFilter} onChange={e => setTierFilter(e.target.value)}
          aria-label="Filter by tier">
          <option value="">All tiers ({labs.length})</option>
          <option value="frontier">Frontier ({tierCounts.frontier || 0})</option>
          <option value="specialist-modal">Specialist · modality ({tierCounts['specialist-modal'] || 0})</option>
          <option value="specialist-vert">Specialist · vertical ({tierCounts['specialist-vert'] || 0})</option>
          <option value="infrastructure">Infrastructure ({tierCounts.infrastructure || 0})</option>
          <option value="regulator">Regulator / AISI ({tierCounts.regulator || 0})</option>
          <option value="alignment">Alignment research ({tierCounts.alignment || 0})</option>
        </select>
        {activeLab && (
          <button type="button" onClick={() => setActiveLab(null)}
            className="btn-secondary text-[10px] inline-flex items-center gap-1.5">
            Clear lab filter ({labsById[activeLab]?.name})
          </button>
        )}
      </div>

      {/* Lab grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filteredLabs.map(l => {
          const newsCount = news.filter(a => a.labs.includes(l.id)).length
          const isActive = activeLab === l.id
          const tierBadge = TIER_BADGE[l.tier || 'lab']
          const onCardKey = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setActiveLab(isActive ? null : l.id)
            }
          }
          return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions
            <div key={l.id}
              role="button" tabIndex={0}
              onClick={() => setActiveLab(isActive ? null : l.id)}
              onKeyDown={onCardKey}
              aria-pressed={isActive ? 'true' : 'false'}
              className={clsx(
                'card p-4 text-left transition-colors group cursor-pointer',
                isActive ? 'border-purple/60 ring-1 ring-purple/40' : 'hover:border-purple/40',
              )}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-base font-bold text-text-primary flex items-center gap-2">
                    <span className="text-lg">{COUNTRY_FLAG[l.country] || '🌐'}</span>
                    {l.name}
                  </div>
                  <div className="text-[10px] text-text-muted mt-0.5">{l.category} · {l.city}</div>
                </div>
                <span className={clsx('badge border shrink-0', tierBadge.cls)} title={tierBadge.title}>
                  {tierBadge.label}
                </span>
              </div>

              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest text-text-muted flex items-center gap-1.5">
                  Latest
                  {l.latest_live ? (
                    <span className="badge text-[8px] bg-emerald/15 text-emerald border border-emerald/30"
                      title={`Source: ${l.latest_source}${l.latest_observed ? ' · observed ' + l.latest_observed.slice(0,10) : ''}`}>
                      live
                    </span>
                  ) : (
                    <span className="badge text-[8px] bg-surface-raised text-text-muted border border-border"
                      title="Static catalogue value — verify against the lab's site">
                      static
                    </span>
                  )}
                </div>
                {l.latest_live ? (
                  <a href={l.latest_live_url || l.homepage} target="_blank" rel="noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="text-sm font-semibold text-emerald font-mono hover:underline block">
                    {l.latest_live}
                  </a>
                ) : (
                  <div className="text-sm font-semibold text-purple-light font-mono">{l.latest}</div>
                )}
                {l.latest_live && l.latest && l.latest_live !== l.latest && (
                  <div className="text-[9px] text-text-muted mt-0.5">
                    catalogue: {l.latest}
                  </div>
                )}
              </div>
              <div className="mt-2">
                <div className="text-[10px] uppercase tracking-widest text-text-muted">Programme</div>
                <div className="text-xs text-text-secondary">{l.programme}</div>
              </div>
              <div className="mt-2">
                <div className="text-[10px] uppercase tracking-widest text-text-muted">Models</div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {l.models.slice(0, 4).map(m => (
                    <span key={m} className="badge bg-surface-raised text-text-secondary text-[9px]">{m}</span>
                  ))}
                  {l.models.length > 4 && (
                    <span className="text-[9px] text-text-muted">+{l.models.length - 4}</span>
                  )}
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-[10px]">
                <span className="text-text-muted">
                  Founded {l.founded} · {l.founders[0]}
                </span>
                <span className="flex items-center gap-2">
                  <a href={`https://github.com/${l.github_org}`} target="_blank" rel="noreferrer"
                    className="text-text-muted hover:text-text-primary inline-flex items-center gap-1"
                    onClick={e => e.stopPropagation()}>
                    <Github size={10} /> {l.github_org}
                  </a>
                  <a href={l.homepage} target="_blank" rel="noreferrer"
                    aria-label={`${l.name} homepage`} title={`${l.name} homepage`}
                    className="text-blue-light hover:underline inline-flex items-center gap-1"
                    onClick={e => e.stopPropagation()}>
                    <ExternalLink size={10} />
                  </a>
                </span>
              </div>
              {newsCount > 0 && (
                <div className="mt-2 text-[10px] text-amber-light">
                  {newsCount} recent article{newsCount === 1 ? '' : 's'}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* News feed */}
      <section className="card p-0">
        <header className="p-3 border-b border-border flex items-center gap-2 flex-wrap">
          <Newspaper size={14} className="text-amber-light" />
          <h2 className="text-sm font-semibold uppercase tracking-widest">
            Frontier news{activeLab ? ` · ${labsById[activeLab]?.name}` : ''}
          </h2>
          <span className="text-[10px] text-text-muted">
            {filteredNews.length} of {news.length} articles
          </span>
        </header>
        <div className="max-h-[560px] overflow-auto">
          {filteredNews.length === 0 ? (
            <div className="p-8 text-center text-text-muted italic text-xs">
              No matching articles in the last ingest cycle.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {filteredNews.map(a => (
                <li key={a.id} className="p-3 hover:bg-surface-raised/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="badge bg-surface-raised text-text-muted">{a.source}</span>
                        <span className="badge bg-blue/10 text-blue-light border border-blue/30">{a.category}</span>
                        {a.labs.slice(0, 3).map(id => (
                          <button key={id} type="button"
                            onClick={() => setActiveLab(id)}
                            className="badge bg-purple/10 text-purple-light border border-purple/30 hover:bg-purple/20">
                            {labsById[id]?.name || id}
                          </button>
                        ))}
                      </div>
                      {a.url ? (
                        <a href={a.url} target="_blank" rel="noreferrer"
                          className="text-sm text-text-primary hover:text-blue-light hover:underline">
                          {a.title}
                        </a>
                      ) : (
                        <span className="text-sm text-text-primary">{a.title}</span>
                      )}
                      {a.summary && (
                        <p className="text-xs text-text-secondary mt-1 line-clamp-2">{a.summary}</p>
                      )}
                    </div>
                    <span className="text-[10px] text-text-muted font-mono shrink-0">
                      {a.published_at?.slice(0, 10)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  )
}

function Tile({ label, value, tone }: { label: string; value: number; tone?: 'emerald' }) {
  const c = tone === 'emerald' ? 'text-emerald' : 'text-purple-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-black font-mono mt-1', c)}>{value.toLocaleString()}</div>
    </div>
  )
}
